import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiGet } from 'api/service';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import {
  alpha,
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
  styled
} from '@mui/material';
import {
  Autorenew as AutorenewIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  Dns as DnsIcon,
  PhoneIphone as PhoneIphoneIcon,
  Public as PublicIcon,
  QueryStats as QueryStatsIcon,
  Search as SearchIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

const BRAND = {
  primary: '#22c55e',
  primaryDark: '#15803d',
  critical: '#ef4444'
};

const HeroCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(BRAND.primary, theme.palette.mode === 'dark' ? 0.52 : 0.34)}`,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 12px 24px rgba(2, 8, 23, 0.32)'
      : '0 10px 22px rgba(15, 23, 42, 0.08)',
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(15,23,42,0.86) 30%, rgba(15,23,42,0.78) 100%)'
      : 'linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(255,255,255,0.98) 34%, rgba(248,250,252,0.95) 100%)'
}));

const PAGE_META = [
  {
    test: (path) => ['/web-scans', '/network-scans', '/cloud-scans', '/cloud-service-scans', '/mobile-scans', '/oast-monitor', '/schedules', '/jobs'].includes(path),
    title: 'FuzzScan Scanning Mission Control',
    subtitle: 'Execute scans with consistent runtime visibility and operational context.',
    actions: [
      { label: 'Web Scans', to: '/web-scans' },
      { label: 'Network Scans', to: '/network-scans' },
      { label: 'Cloud Scans', to: '/cloud-scans' },
      { label: 'Vulnerabilities', to: '/vulns' }
    ]
  },
  {
    test: (path) => ['/recon', '/enum', '/network-map'].includes(path),
    title: 'FuzzScan Discovery Mission Control',
    subtitle: 'Drive reconnaissance and mapping with a unified operator experience.',
    actions: [
      { label: 'Recon', to: '/recon' },
      { label: 'Enumeration', to: '/enum' },
      { label: 'Network Map', to: '/network-map' }
    ]
  },
  {
    test: (path) => ['/vulns', '/remediation', '/cves', '/compliance', '/vex', '/risk'].includes(path),
    title: 'FuzzScan Vulnerability Mission Control',
    subtitle: 'Prioritize risk and remediation with shared vulnerability intelligence.',
    actions: [
      { label: 'Findings', to: '/vulns' },
      { label: 'Remediation', to: '/remediation' },
      { label: 'CVE Catalog', to: '/cves' }
    ]
  },
  {
    test: (path) => ['/cases', '/intel/feeds'].includes(path),
    title: 'FuzzScan Intel & Cases Mission Control',
    subtitle: 'Manage investigations and intelligence workflows with consistent tooling.',
    actions: [
      { label: 'Cases', to: '/cases' },
      { label: 'Intel Feeds', to: '/intel/feeds' },
      { label: 'Risk Overview', to: '/risk' }
    ]
  },
  {
    test: (path) => ['/reporting', '/analytics', '/artifacts', '/supply-chain', '/finding-fingerprints'].includes(path),
    title: 'FuzzScan Reporting Mission Control',
    subtitle: 'Track program posture, artifacts, and operational KPIs in one place.',
    actions: [
      { label: 'Reporting', to: '/reporting' },
      { label: 'Analytics', to: '/analytics' },
      { label: 'Artifacts', to: '/artifacts' }
    ]
  },
  {
    test: (path) => ['/settings', '/settings/api-keys', '/settings/web-auth-profiles'].includes(path),
    title: 'FuzzScan Settings Mission Control',
    subtitle: 'Manage user configuration, credentials, and scan profile behavior.',
    actions: [
      { label: 'Account', to: '/settings' },
      { label: 'API Keys', to: '/settings/api-keys' },
      { label: 'Auth Profiles', to: '/settings/web-auth-profiles' }
    ]
  }
];

const shouldRenderHeader = (path) => {
  if (!path) return false;
  if (path === '/' || path === '/dashboard' || path === '/targets') return false;
  if (path.startsWith('/admin')) return false;
  return PAGE_META.some((entry) => entry.test(path));
};

const resolvePageMeta = (path) => PAGE_META.find((entry) => entry.test(path)) || null;

export default function UniversalMissionHeader() {
  const location = useLocation();
  const { currentTargetId, targets = [], setCurrentTargetId } = useFuzzScanTarget();
  const [runtime, setRuntime] = useState({ running: 0, done: 0, failed: 0 });
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [targetQuery, setTargetQuery] = useState('');

  const pathname = location?.pathname || '';
  const visible = shouldRenderHeader(pathname);
  const meta = useMemo(() => resolvePageMeta(pathname), [pathname]);

  useEffect(() => {
    if (!visible) return;
    let isMounted = true;

    const loadRuntime = async () => {
      try {
        const scanQuery = currentTargetId ? `/api/scans/?user_target=${currentTargetId}` : '/api/scans/';
        const data = await apiGet(scanQuery);
        const rows = Array.isArray(data) ? data : data?.results || [];
        const summary = rows.slice(0, 100).reduce(
          (acc, row) => {
            const status = String(row?.status || '').toLowerCase();
            if (status === 'failed') acc.failed += 1;
            else if (status === 'completed' || status === 'finished' || status === 'success') acc.done += 1;
            else if (status === 'running' || status === 'pending') acc.running += 1;
            return acc;
          },
          { running: 0, done: 0, failed: 0 }
        );
        if (isMounted) setRuntime(summary);
      } catch {
        if (isMounted) setRuntime({ running: 0, done: 0, failed: 0 });
      }
    };

    loadRuntime();
    return () => {
      isMounted = false;
    };
  }, [visible, currentTargetId, pathname]);

  useEffect(() => {
    if (!visible) return undefined;
    const handleKeydown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setTargetDialogOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [visible]);

  const targetOptions = useMemo(
    () =>
      (targets || []).map((row) => ({
        id: row.id,
        label: row.name || row.target?.name || row.target?.value || `Target #${row.id}`,
        value: row.target?.value || '',
        type: row.target?.type || '',
        platform: row.target?.platform || ''
      })),
    [targets]
  );

  const selectedTarget = useMemo(
    () => targetOptions.find((target) => String(target.id) === String(currentTargetId)) || null,
    [currentTargetId, targetOptions]
  );

  const filteredTargets = useMemo(() => {
    const query = targetQuery.trim().toLowerCase();
    if (!query) return targetOptions.slice(0, 60);
    return targetOptions
      .filter((target) => `${target.label || ''} ${target.value || ''} ${target.type || ''}`.toLowerCase().includes(query))
      .slice(0, 80);
  }, [targetOptions, targetQuery]);

  const renderTargetIcon = (target) => {
    const type = String(target?.type || '').toLowerCase();
    if (type === 'mobile') return <PhoneIphoneIcon fontSize="small" />;
    if (type === 'ip') return <DnsIcon fontSize="small" />;
    return <PublicIcon fontSize="small" />;
  };

  const handleSelectTarget = (id) => {
    setCurrentTargetId(String(id));
    setTargetDialogOpen(false);
    setTargetQuery('');
  };

  if (!visible || !meta) return null;

  return (
    <>
      <HeroCard className="scanner-pro-hero" sx={{ mb: 2.2 }}>
        <CardContent sx={{ p: 2.2 }}>
          <Grid container spacing={2} alignItems="stretch">
            <Grid size={{ xs: 12, xl: 5.3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                <SecurityIcon sx={{ color: BRAND.primaryDark, fontSize: 24 }} />
                <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.8rem' } }}>
                  {meta.title}
                </Typography>
                <Chip size="small" label="Live" sx={{ color: BRAND.primaryDark, bgcolor: alpha(BRAND.primary, 0.2), fontWeight: 700 }} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {meta.subtitle}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6, xl: 3.2 }}>
              <Box
                sx={{
                  border: `1px solid ${alpha(BRAND.primary, 0.42)}`,
                  borderRadius: 2.2,
                  p: 1.5,
                  height: '100%',
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha('#0f172a', 0.58) : alpha('#f8fafc', 0.72))
                }}
              >
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em', fontWeight: 700 }}>
                  Target Selection
                </Typography>
                <ButtonBase
                  onClick={() => setTargetDialogOpen(true)}
                  sx={{
                    mt: 0.8,
                    width: '100%',
                    borderRadius: 2,
                    border: `1px dashed ${alpha(BRAND.primary, 0.52)}`,
                    px: 1.2,
                    py: 1,
                    textAlign: 'left',
                    display: 'block',
                    bgcolor: alpha(BRAND.primary, 0.05),
                    '&:hover': {
                      bgcolor: alpha(BRAND.primary, 0.1),
                      borderColor: alpha(BRAND.primary, 0.76)
                    }
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: BRAND.primary,
                        bgcolor: alpha(BRAND.primary, 0.14),
                        flexShrink: 0
                      }}
                    >
                      {selectedTarget ? renderTargetIcon(selectedTarget) : <SearchIcon fontSize="small" />}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                        {selectedTarget?.label || 'Select target'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {selectedTarget?.value || 'Choose the active scan context'}
                      </Typography>
                    </Box>
                    <Chip size="small" label="Ctrl/⌘ K" variant="outlined" sx={{ borderColor: alpha(BRAND.primary, 0.35), fontSize: '0.68rem' }} />
                  </Stack>
                </ButtonBase>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6, xl: 3.5 }}>
              <Box
                sx={{
                  border: `1px solid ${alpha(BRAND.primary, 0.55)}`,
                  borderRadius: 2.2,
                  p: 1.5,
                  height: '100%',
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha('#0f172a', 0.7) : alpha('#f1f5f9', 0.7))
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em', fontWeight: 700 }}>
                    Runtime Snapshot
                  </Typography>
                  <QueryStatsIcon sx={{ color: '#0ea5e9', fontSize: 18 }} />
                </Stack>
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 0.8, mb: 1.2 }}>
                  <Chip
                    size="small"
                    icon={<AutorenewIcon fontSize="small" />}
                    label={`${runtime.running} Running`}
                    sx={{ bgcolor: alpha('#38bdf8', 0.18), color: '#0284c7', fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    icon={<CheckCircleOutlineIcon fontSize="small" />}
                    label={`${runtime.done} Done`}
                    sx={{ bgcolor: alpha(BRAND.primary, 0.2), color: BRAND.primaryDark, fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    icon={<ErrorOutlineIcon fontSize="small" />}
                    label={`${runtime.failed} Failed`}
                    sx={{ bgcolor: alpha(BRAND.critical, 0.18), color: BRAND.critical, fontWeight: 700 }}
                  />
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Target context: {selectedTarget?.label || (currentTargetId ? `#${currentTargetId}` : 'All targets')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </HeroCard>

      <Dialog
        open={targetDialogOpen}
        onClose={() => setTargetDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(BRAND.primary, 0.34)}`,
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.98)')
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Select Active Target
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.4}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={targetQuery}
              onChange={(event) => setTargetQuery(event.target.value)}
              placeholder="Search target name, domain, URL, IP..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <Box
              sx={{
                border: `1px solid ${alpha(BRAND.primary, 0.18)}`,
                borderRadius: 2,
                maxHeight: 360,
                overflow: 'auto'
              }}
            >
              {!filteredTargets.length ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No target matches your search.
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {filteredTargets.map((target) => {
                    const selected = String(target.id) === String(currentTargetId);
                    return (
                      <ListItemButton
                        key={target.id}
                        selected={selected}
                        onClick={() => handleSelectTarget(target.id)}
                        sx={{
                          py: 1,
                          borderBottom: `1px solid ${alpha(BRAND.primary, 0.1)}`,
                          '&.Mui-selected': {
                            bgcolor: `${alpha(BRAND.primary, 0.12)} !important`
                          }
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: '50%',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: selected ? BRAND.primary : 'text.secondary',
                              bgcolor: alpha(BRAND.primary, selected ? 0.18 : 0.08),
                              flexShrink: 0
                            }}
                          >
                            {renderTargetIcon(target)}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                              {target.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {target.value || target.type || `Target #${target.id}`}
                            </Typography>
                          </Box>
                          <Chip size="small" variant="outlined" label={target.type || 'target'} />
                        </Stack>
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
